import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Card,
  Table,
  TableBody,
  TableContainer,
  Button,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Alert,
  Box,
  CircularProgress
} from '@mui/material';
import Scrollbar from 'src/components/scrollbar';
import Iconify from 'src/components/iconify';
import axiosInstance, { endpoints } from 'src/utils/axios';
import { useSnackbar } from 'src/components/snackbar';
import { useBoolean } from 'src/hooks/use-boolean';
import {
  emptyRows,
  TableNoData,
  TableEmptyRows,
  TableHeadCustom,
  TableSelectedAction,
  TablePaginationCustom,
} from 'src/components/table';
import { ConfirmDialog } from 'src/components/custom-dialog';
import GenericTableRow from './GenericTableRow';

export default function GenericTable({
  config,
  data,
  totalCount,
  table,
  onEdit,
  onDuplicate,
  onDelete,
  onBulkDelete,
  onDeleteAll,
  onBulkAction,
  bulkActionLabel,
  visibleColumns,
  columnOrder,
  features,
  form,
  entityConfig,
  isLoading,
  sortField,
  sortDirection,
  onSort,
  currentUserId,
  currentUser,
  limitReached,
}) {
  const confirm = useBoolean();
  const confirmAll = useBoolean();

  // Build visible table headers from columnOrder
  const visibleTableHead = [
    { id: 'srNo', label: 'Sr. No.', width: 80, align: 'center', sortable: false },
    ...columnOrder
      .filter((col) => visibleColumns[col.id])
      .map((col) => ({
        id: col.id,
        label: col.label,
        width: col.width,
        align: col.align || 'left',
        sortable: col.sortable !== undefined ? col.sortable : true,
      })),
    { id: '', width: 88, sortable: false }, // Actions column
  ];

  const denseHeight = table.dense ? 52 : 72;
  const notFound = !data.length && !isLoading;

  const selectableRowCount = features.isSelectable
    ? data.filter((row) => features.isSelectable(row)).length
    : data.length;

  const handleBulkDelete = async () => {
    if (onBulkDelete) {
      await onBulkDelete(table.selected);
      table.setSelected([]);
      confirm.onFalse();
    }
  };

  const showCheckbox = features.select !== false && features.isSelectable !== false;

  return (
    <>
      <Card>
        <TableContainer sx={{ position: 'relative', overflow: 'unset' }}>
          {showCheckbox && (
            <TableSelectedAction
              dense={table.dense}
              numSelected={table.selected.length}
              rowCount={selectableRowCount}
              onSelectAllRows={(checked) =>
                table.onSelectAllRows(
                  checked,
                  data
                    .filter((row) => !features.isSelectable || features.isSelectable(row))
                    .map((row) => row.id)
                )
              }
              action={
                <Stack direction="row" spacing={1}>
                  {features.bulkApprove && config.onBulkApprove && (
                    <Button
                      color="success"
                      onClick={() => config.onBulkApprove(table.selected)}
                      startIcon={<Iconify icon="solar:check-circle-bold" />}
                    >
                      Approve ({table.selected.length})
                    </Button>
                  )}
                  {features.bulkReject && config.onBulkReject && (
                    <Button
                      color="error"
                      onClick={() => config.onBulkReject(table.selected)}
                      startIcon={<Iconify icon="solar:close-circle-bold" />}
                    >
                      Reject ({table.selected.length})
                    </Button>
                  )}
                  {onBulkAction && bulkActionLabel && (
                    <Button
                      color="success"
                      onClick={() => onBulkAction(table.selected)}
                      startIcon={<Iconify icon="solar:check-circle-bold" />}
                    >
                      {bulkActionLabel}
                    </Button>
                  )}
                  {features.bulkDelete !== false && (features.isAdmin || features.deleteAll || features.deleteSelf) && (() => {
                    const selectedRows = data.filter((row) => table.selected.includes(row.id));
                    const allSelectedAreDeletable = selectedRows.length > 0 && selectedRows.every((row) => {
                      const owner = row.createdBy || row.userId || row.expense_by;
                      const ownerId = typeof owner === 'object' ? (owner?._id || owner?.id) : owner;
                      
                      return features.isAdmin || 
                      features.deleteAll || 
                      (features.deleteSelf && ownerId === currentUserId)
                    });
                    return allSelectedAreDeletable ? (
                      <Button
                        color="error"
                        onClick={confirm.onTrue}
                        startIcon={<Iconify icon="solar:trash-bin-trash-bold" />}
                      >
                        Delete
                      </Button>
                    ) : null;
                  })()}
                  {features.deleteAll && (
                    <Button
                      color="error"
                      variant="outlined"
                      onClick={confirmAll.onTrue}
                      startIcon={<Iconify icon="solar:trash-bin-minimalistic-bold" />}
                    >
                      Delete All ({totalCount})
                    </Button>
                  )}
                </Stack>
              }
            />
          )}

          <Scrollbar>
            <Table size={table.dense ? 'small' : 'medium'} sx={{ minWidth: 960 }}>
              <TableHeadCustom
                order={sortDirection}
                orderBy={sortField}
                headLabel={visibleTableHead}
                rowCount={selectableRowCount}
                numSelected={table.selected.length}
                onSort={onSort}
                onSelectAllRows={showCheckbox ? (checked) =>
                  table.onSelectAllRows(
                    checked,
                    data
                      .filter((row) => !features.isSelectable || features.isSelectable(row))
                      .map((row) => row.id)
                  ) : undefined}
              />

              <TableBody>
                {data.map((row, index) => (
                  <GenericTableRow
                    key={row.id}
                    row={row}
                    rowIndex={(table.page * table.rowsPerPage) + index + 1}
                    selected={table.selected.includes(row.id)}
                    onSelectRow={() => table.onSelectRow(row.id)}
                    onEditRow={() => onEdit(row.id)}
                    onDuplicateRow={() => onDuplicate(row.id)}
                    onDeleteRow={() => onDelete(row.id)}
                    config={config}
                    visibleColumns={visibleColumns}
                    columnOrder={columnOrder.map((col) => col.id)}
                    features={features}
                    currentUserId={currentUserId}
                    currentUser={currentUser}
                    limitReached={limitReached}
                  />
                ))}

                <TableEmptyRows
                  height={denseHeight}
                  emptyRows={emptyRows(table.page, table.rowsPerPage, totalCount)}
                  colSpan={visibleTableHead.length + (showCheckbox ? 1 : 0)}
                />

                <TableNoData 
                  notFound={notFound} 
                  colSpan={visibleTableHead.length + (showCheckbox ? 1 : 0)} 
                />
              </TableBody>
            </Table>
          </Scrollbar>
        </TableContainer>

        <TablePaginationCustom
          count={totalCount}
          page={table.page}
          rowsPerPage={table.rowsPerPage}
          onPageChange={table.onChangePage}
          onRowsPerPageChange={table.onChangeRowsPerPage}
          dense={table.dense}
          onChangeDense={table.onChangeDense}
        />
      </Card>

      <ConfirmDialog
        open={confirm.value}
        onClose={confirm.onFalse}
        title="Delete"
        content={`Are you sure you want to delete ${table.selected.length} items?`}
        action={
          <Button variant="contained" color="error" onClick={handleBulkDelete}>
            Delete
          </Button>
        }
      />
      <DeleteAllDataDialog
        open={confirmAll.value}
        onClose={confirmAll.onFalse}
        form={form}
        entityConfig={entityConfig}
        totalCount={totalCount}
        onDeleteAll={() => {
          if (onDeleteAll) {
            onDeleteAll();
            table.setSelected([]);
            confirmAll.onFalse();
          }
        }}
      />
    </>
  );
}

// ----------------------------------------------------------------------

function DeleteAllDataDialog({ open, onClose, form, entityConfig, totalCount, onDeleteAll }) {
  const { enqueueSnackbar } = useSnackbar();
  const [isExported, setIsExported] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setIsExported(!!form?.lastExportedAt);
      setError(null);
    }
  }, [open, form]);

  const handleExport = async () => {
    try {
      setExporting(true);
      setError(null);

      if (form?._id) {
        // Dynamic form export via backend endpoint
        const response = await axiosInstance.get(`${endpoints.formData.root}/export`, {
          params: { formId: form._id, format: 'excel' },
          responseType: 'blob'
        });

        let filename = '';
        const contentDisposition = response.headers['content-disposition'];
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename=(.+)/);
          if (filenameMatch && filenameMatch.length > 1) {
            filename = filenameMatch[1].replace(/['"]/g, '');
          }
        }
        if (!filename) {
          filename = `${form?.name?.toLowerCase().replace(/\s+/g, '-')}-data.zip`;
        }

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);

        await axiosInstance.put(endpoints.forms.markAsExported(form._id));
      } else if (entityConfig) {
        // Standard entity: client-side export via xlsx
        const listEndpoint = entityConfig.entity?.apiEndpoint?.list;
        if (!listEndpoint) throw new Error('No list endpoint configured');

        let fetchResponse;
        try {
          fetchResponse = await axiosInstance.post(listEndpoint, { page: 1, limit: 0 });
        } catch {
          fetchResponse = await axiosInstance.get(listEndpoint, { params: { page: 1, limit: 0 } });
        }

        let rowData = fetchResponse.data?.data || [];
        if (entityConfig.transformTableData) {
          rowData = entityConfig.transformTableData(rowData);
        }

        const exportCols = entityConfig.table?.columns || [];
        /* eslint-disable no-nested-ternary */
        const formattedData = rowData.map(row => {
          const newRow = {};
          exportCols.forEach(col => {
            let val = row[col.id];

            if (val === null || val === undefined) {
              val = '';
            } else if (Array.isArray(val)) {
              val = val.map(v => {
                if (typeof v === 'object' && v !== null) {
                  return v.state_name || v.city_name || v.name || v.label || v.title || v._id || String(v);
                }
                return String(v);
              }).join(', ');
              if (!val) {
                if (col.id === 'states') val = 'All States';
                else if (col.id === 'cities') val = 'All Cities';
                else val = '';
              }
            } else if (val instanceof Date) {
              val = val.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
            } else if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(val)) {
              val = new Date(val).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
            } else if (typeof val === 'object' && val !== null) {
              val = val.name || val.label || val.title || val.state_name || val.city_name || JSON.stringify(val);
            }

            newRow[col.label || col.id] = val;
          });
          return newRow;
        });
        /* eslint-enable no-nested-ternary */

        const { utils, writeFile } = await import('xlsx');
        const ws = utils.json_to_sheet(formattedData);
        const wb = utils.book_new();
        const sheetName = entityConfig.entity?.namePlural || 'Data';
        utils.book_append_sheet(wb, ws, sheetName);

        const entityFileName = (entityConfig.entity?.namePlural || 'data').toLowerCase().replace(/\s+/g, '-');
        writeFile(wb, `${entityFileName}-export.xlsx`);
      } else {
        throw new Error('No export configuration available');
      }

      setIsExported(true);
      enqueueSnackbar('Data exported successfully. You can now delete all records.', { variant: 'success' });
    } catch (err) {
      console.error('Export error:', err);
      setError('Failed to export data. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const handleDelete = async () => {
    try {
      setDeleting(true);
      setError(null);
      await onDeleteAll();
    } catch (err) {
      console.error('Delete error:', err);
      setError(err.message || 'Failed to delete data.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ color: 'error.main', display: 'flex', alignItems: 'center', gap: 1 }}>
        <Iconify icon="solar:danger-bold" width={24} />
        Delete All Records: {form?.name}
      </DialogTitle>

      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          <Typography variant="body1" fontWeight={600}>
            Safe Deletion Protocol
          </Typography>

          <Alert severity="warning">
            You are about to delete <strong>ALL {totalCount}</strong> records for this form.
            To ensure no data loss, you <strong>must download</strong> the data before deleting.
          </Alert>

          <Stack direction="row" alignItems="center" spacing={2}>
            <Box sx={{ 
              width: 32, height: 32, borderRadius: '50%', bgcolor: isExported ? 'success.main' : 'primary.main', 
              color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold'
            }}>
              {isExported ? <Iconify icon="eva:checkmark-fill" /> : '1'}
            </Box>
            <Stack flex={1}>
              <Typography variant="subtitle2">Download All Data</Typography>
              <Typography variant="caption" color="text.secondary">
                {isExported ? `Last exported: ${new Date(form?.lastExportedAt || Date.now()).toLocaleString()}` : 'Requirement for deletion'}
              </Typography>
            </Stack>
            <Button
              variant="outlined"
              size="small"
              onClick={handleExport}
              disabled={exporting || deleting}
              startIcon={exporting ? <CircularProgress size={16} /> : <Iconify icon="solar:download-bold" />}
            >
              {exporting ? 'Exporting...' : 'Export & Download'}
            </Button>
          </Stack>

          <Stack direction="row" alignItems="center" spacing={2} sx={{ opacity: isExported ? 1 : 0.5 }}>
            <Box sx={{ 
              width: 32, height: 32, borderRadius: '50%', bgcolor: 'error.main', 
              color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold'
            }}>
              2
            </Box>
            <Stack flex={1}>
              <Typography variant="subtitle2">Confirm Deletion</Typography>
              <Typography variant="caption" color="text.secondary">Mark all records as deleted (soft-delete)</Typography>
            </Stack>
          </Stack>

          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={deleting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleDelete}
          disabled={!isExported || deleting}
          startIcon={deleting ? <CircularProgress size={20} /> : <Iconify icon="solar:trash-bin-trash-bold" />}
        >
          {deleting ? 'Deleting...' : `Delete ${totalCount} Records`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

DeleteAllDataDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  form: PropTypes.object,
  entityConfig: PropTypes.object,
  totalCount: PropTypes.number,
  onDeleteAll: PropTypes.func,
};

GenericTable.propTypes = {
  config: PropTypes.object.isRequired,
  data: PropTypes.array.isRequired,
  totalCount: PropTypes.number.isRequired,
  table: PropTypes.object.isRequired,
  form: PropTypes.object,
  entityConfig: PropTypes.object,
  onEdit: PropTypes.func.isRequired,
  onDuplicate: PropTypes.func,
  onDelete: PropTypes.func.isRequired,
  onBulkDelete: PropTypes.func,
  onDeleteAll: PropTypes.func,
  onBulkAction: PropTypes.func,
  bulkActionLabel: PropTypes.string,
  visibleColumns: PropTypes.object.isRequired,
  columnOrder: PropTypes.array.isRequired,
  features: PropTypes.object,
  isLoading: PropTypes.bool,
  sortField: PropTypes.string,
  sortDirection: PropTypes.oneOf(['asc', 'desc']),
  onSort: PropTypes.func,
  currentUserId: PropTypes.string,
  currentUser: PropTypes.object,
  limitReached: PropTypes.bool,
};
